import React, { useEffect, useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Chip,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useLazyGetProvinceWeeklyAverageSalesQuery } from "store/services/reportApi";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { CalendarMonth } from "@mui/icons-material";

const SalesDayWiseGraph = ({
    account,
    entity,
    branch,
    user,
    payer,
    customer,
    vendor,
    responsibleUser,
    region,
    paymentStatus,
    fromDate,
    toDate,
}) => {
    const [
        fetchProvinceAverage,
        { data: provinceData, isLoading, isFetching, error },
    ] = useLazyGetProvinceWeeklyAverageSalesQuery();

    useEffect(() => {
        if (!account || !entity) return;

        fetchProvinceAverage({
            params: {
                account,
                entity,
                branch: branch !== "all" ? branch : undefined,
                user: user !== "all" ? user : undefined,
                payer: payer !== "all" ? payer : undefined,
                customer: customer !== "all" ? customer : undefined,
                vendor: vendor !== "all" ? vendor : undefined,
                responsibleUser:
                    responsibleUser !== "all" ? responsibleUser : undefined,
                region: region !== "all" ? region : undefined,
                paymentStatus: paymentStatus !== "all" ? paymentStatus : undefined,
                fromDate,
                toDate,
                sortedBy: "weeklyAverageSales",
                sortDirection: -1, // Descending order
            },
        });
    }, [
        account,
        entity,
        branch,
        user,
        payer,
        customer,
        vendor,
        responsibleUser,
        region,
        paymentStatus,
        fromDate,
        toDate,
        fetchProvinceAverage,
    ]);

    const chartData = useMemo(() => {
        if (!provinceData?.data) return { provinces: [], sales: [] };

        const provinces = provinceData.data.map((item) => item.regionName || "N/A");
        const sales = provinceData.data.map((item) => item.weeklyAverageSales || 0);

        return { provinces, sales };
    }, [provinceData]);

    const valueFormatter = (value) => `Rs. ${value?.toFixed(2) || "0.00"}`;

    if (isLoading || isFetching) {
        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={300}
                    >
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Alert severity="error">
                        {error?.data?.message ||
                            "Failed to load province weekly average sales data"}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (!provinceData?.data || provinceData.data.length === 0) {
        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Alert severity="info">
                        No data available for the selected filters. Try adjusting your date
                        range or filters.
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            {/* Bar Chart */}
            <Card>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <ShowChartIcon sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="h6" fontWeight={600}>
                            Province Weekly Average Sales
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<CalendarMonth />}
                            label={`From: ${fromDate}`}
                            variant="outlined"
                            color="primary"
                        />
                        <Chip
                            icon={<CalendarMonth />}
                            label={`To: ${toDate}`}
                            variant="outlined"
                            color="primary"
                        />
                    </Box>
                    <BarChart
                        xAxis={[
                            {
                                scaleType: "band",
                                data: chartData.provinces,
                                label: "Province / Region",
                                labelStyle: { fontSize: 14, fontWeight: 600 },
                            },
                        ]}
                        yAxis={[
                            { label: "", labelStyle: { fontSize: 4 } },
                        ]}
                        series={[
                            {
                                data: chartData.sales,
                                label: "Weekly Total Sales Average",
                                color: "#1976d2",
                                valueFormatter,
                            },
                        ]}
                        height={450}
                        margin={{ top: 50, right: 30, bottom: 80, left: 100 }} // ← Extra top space
                        slotProps={{
                            legend: {
                                direction: "row",
                                position: { vertical: "top", horizontal: "center" }, // ← Legend at top center
                                padding: 8,
                                itemGap: 12,
                            },
                        }}
                    />

                </CardContent>
            </Card>
        </Box>
    );
};

export default SalesDayWiseGraph;

